import type {
  MissionAssessmentDraftCreateDto,
  MissionAssessmentDraftSelectDto,
  MissionAssessmentEffectCreateDto,
  MissionAssessmentExpertNoteDto,
} from '@@types/api.types';
import {
  ExpertNoteIdentifier,
  FieldedStatus,
  MissionAssessmentDraftStatus,
  MissionAssessmentInterType,
  VerificationConfidence,
} from '@@types/api.types';
import {
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  NAME_VALIDATION_MESSAGES,
  SHORT_DESCRIPTION_MAX_LENGTH,
  SHORT_DESCRIPTION_VALIDATION_MESSAGES,
} from '@azymmetric/shared/validation/validation.constants';
import type { MissionAssessmentFormData } from '@schemas/mission-assessment.schema';
import {
  missionAssessmentDataDraftSchema,
  missionAssessmentDataSchema,
  missionAssessmentEffectDataBaseSchema,
  missionAssessmentEffectDataSchema,
} from '@schemas/mission-assessment.schema';
import { z } from 'zod/v4';
import { zCoercedOptional, zFilteredStringArray } from '../utils/zodv4.util';

export type MissionAssessmentDraftBaseDto = Omit<MissionAssessmentDraftCreateDto, 'requestId' | 'technologyId'>;

// Schema that accepts MissionAssessmentFormData and transforms to API format
export const missionAssessmentDraftSchema = z.object({
  capabilities: zFilteredStringArray(z.array(z.uuid())),
  communities: zFilteredStringArray(z.array(z.uuid())),

  data: missionAssessmentDataDraftSchema.optional(),

  effects: z
    .array(
      z.object({
        data: missionAssessmentEffectDataSchema.optional(),
        domainId: zCoercedOptional(z.uuid().optional()),
        effectId: zCoercedOptional(z.uuid().optional()),
        secondaryEffects: z.array(z.uuid()).optional(),
        targetSystems: z.array(z.uuid()).optional(),
        useId: zCoercedOptional(z.uuid().optional()),
      }),
    )
    .optional()
    .transform((effects) =>
      effects?.map((e) => ({
        data: e.data || {},
        domainId: e.domainId,
        effectId: e.effectId,
        secondaryEffects: e.secondaryEffects || [],
        targetSystems: e.targetSystems || [],
        useId: e.useId,
      })),
    ),

  inters: z
    .array(
      z.object({
        categoryId: zCoercedOptional(z.uuid().optional()),
        country: zCoercedOptional(z.string().trim().optional()),
        description: zCoercedOptional(
          z
            .string()
            .trim()
            .max(SHORT_DESCRIPTION_MAX_LENGTH, { message: SHORT_DESCRIPTION_VALIDATION_MESSAGES.max })
            .optional(),
        ),
        digitalIntegrationIds: zFilteredStringArray(z.array(z.uuid())).optional(),
        fieldedStatus: z.nativeEnum(FieldedStatus).optional(),
        mrl: z.number().optional(),
        name: zCoercedOptional(
          z
            .string()
            .min(NAME_MIN_LENGTH, { message: NAME_VALIDATION_MESSAGES.min })
            .max(NAME_MAX_LENGTH, { message: NAME_VALIDATION_MESSAGES.max })
            .optional(),
        ),
        notes: zCoercedOptional(z.string().optional()),
        operationalIntegrationIds: zFilteredStringArray(z.array(z.uuid())).optional(),
        physicalIntegrationIds: zFilteredStringArray(z.array(z.uuid())).optional(),
        priorityRationale: zCoercedOptional(z.string().optional()),
        priorityScore: z.number().optional(),
        reference: zCoercedOptional(z.string().optional()),
        subcategoryId: zCoercedOptional(z.uuid().optional()),
        trl: z.number().optional(),
        type: z.nativeEnum(MissionAssessmentInterType),
        verificationConfidence: z.nativeEnum(VerificationConfidence).optional(),
        verificationNotes: zCoercedOptional(z.string().optional()),
      }),
    )
    .optional()
    .transform((inters) =>
      inters
        ?.filter(
          (i) =>
            i.categoryId ||
            i.country ||
            i.description ||
            i.name ||
            i.notes ||
            i.subcategoryId ||
            (i.digitalIntegrationIds && i.digitalIntegrationIds.length > 0) ||
            (i.operationalIntegrationIds && i.operationalIntegrationIds.length > 0) ||
            (i.physicalIntegrationIds && i.physicalIntegrationIds.length > 0),
        )
        .map((i) => ({
          categoryId: i.categoryId,
          country: i.country,
          description: i.description,
          digitalIntegrationIds:
            i.digitalIntegrationIds && i.digitalIntegrationIds.length > 0 ? i.digitalIntegrationIds : undefined,
          fieldedStatus: i.fieldedStatus,
          mrl: i.mrl,
          name: i.name,
          notes: i.notes,
          operationalIntegrationIds:
            i.operationalIntegrationIds && i.operationalIntegrationIds.length > 0
              ? i.operationalIntegrationIds
              : undefined,
          physicalIntegrationIds:
            i.physicalIntegrationIds && i.physicalIntegrationIds.length > 0 ? i.physicalIntegrationIds : undefined,
          priorityRationale: i.priorityRationale || '',
          priorityScore: i.priorityScore,
          reference: i.reference || '',
          subcategoryId: i.subcategoryId,
          trl: i.trl,
          type: i.type,
          verificationConfidence: i.verificationConfidence,
          verificationNotes: i.verificationNotes || '',
        })),
    ),

  killChains: zFilteredStringArray(z.array(z.uuid())),

  recommendedDomains: zFilteredStringArray(z.array(z.uuid())),

  recommendedServices: z
    .array(
      z.object({
        serviceId: zCoercedOptional(z.uuid().optional()),
        unit: zCoercedOptional(z.string().optional()),
      }),
    )
    .optional(),

  recommendedTechnologyUses: z
    .array(
      z.object({
        description: zCoercedOptional(
          z
            .string()
            .trim()
            .max(SHORT_DESCRIPTION_MAX_LENGTH, { message: SHORT_DESCRIPTION_VALIDATION_MESSAGES.max })
            .optional(),
        ),
        domains: z.array(z.object({ domainId: z.uuid() })),
        useId: z.uuid(),
      }),
    )
    .optional()
    .transform((techUses) => {
      if (!techUses || techUses.length === 0) return undefined;

      // Deduplicate by useId, merging domains
      const useMap = new Map<string, { description?: string; domains: { domainId: string }[]; useId: string }>();

      for (const techUse of techUses) {
        const existing = useMap.get(techUse.useId);
        if (existing) {
          // Merge domains, deduplicating by domainId
          const existingDomainIds = new Set(existing.domains.map((d) => d.domainId));
          const newDomains = techUse.domains.filter((d) => !existingDomainIds.has(d.domainId));
          existing.domains = [...existing.domains, ...newDomains];
          if (!existing.description && techUse.description) {
            existing.description = techUse.description;
          }
        } else {
          // Deduplicate domains within this tech use
          const seenDomainIds = new Set<string>();
          const uniqueDomains = techUse.domains.filter((d) => {
            if (seenDomainIds.has(d.domainId)) return false;
            seenDomainIds.add(d.domainId);
            return true;
          });
          useMap.set(techUse.useId, { ...techUse, domains: uniqueDomains });
        }
      }

      // Transform to API format with domainIds
      return Array.from(useMap.values()).map((tu) => ({
        description: tu.description,
        domainIds: tu.domains.map((d) => d.domainId),
        useId: tu.useId,
      }));
    }),

  recommendedUses: zFilteredStringArray(z.array(z.uuid())),

  status: z.enum(MissionAssessmentDraftStatus).optional(),

  taskRecommendations: z
    .array(
      z.object({
        description: zCoercedOptional(
          z.string().max(1000, { message: 'Task recommendation must be 1000 characters or less' }).optional(),
        ),
      }),
    )
    .default([])
    .transform((taskRecommendations) =>
      taskRecommendations
        ?.filter((t) => t.description && t.description.trim() !== '')
        .map((t) => ({
          description: t.description?.trim() || '',
        })),
    ),
}) satisfies z.ZodType<MissionAssessmentDraftBaseDto>;

// Create/Update schema for drafts (frontend form → API payload)
export const missionAssessmentDraftCreateSchema = missionAssessmentDraftSchema.check((ctx) => {
  const data = ctx.value;

  // When publishing, require essential fields
  if (data.status === MissionAssessmentDraftStatus.COMPLETE) {
    if (!data.capabilities || data.capabilities.length === 0) {
      ctx.issues.push({
        code: 'custom',
        message: 'At least one capability must be selected',
        path: ['capabilities'],
        input: ctx.value,
      });
    }

    if (!data.communities || data.communities.length === 0) {
      ctx.issues.push({
        code: 'custom',
        message: 'At least one community must be selected',
        path: ['communities'],
        input: ctx.value,
      });
    }
  }
});

// -----------------------------------------------------------------------------
// Conversion: DraftSelectDto → MissionAssessmentFormData (for editing drafts)
// -----------------------------------------------------------------------------

export const convertMissionAssessmentDraftToFormData = (
  draft: MissionAssessmentDraftSelectDto,
): MissionAssessmentFormData => {
  const parsedData = missionAssessmentDataSchema.safeParse(draft.data || {});

  // Ensure expertNotes array has all identifiers present for UI rendering
  const identifiers = [
    ExpertNoteIdentifier.THREATS,
    ExpertNoteIdentifier.STRATEGIC,
    ExpertNoteIdentifier.RISKS,
    ExpertNoteIdentifier.SUSTAINMENT,
  ];

  const existingNotes = parsedData.data?.expertNotes || [];
  const notesById = new Map<ExpertNoteIdentifier, MissionAssessmentExpertNoteDto>(
    existingNotes.map((n) => [n.identifier, n]),
  );

  const filledExpertNotes = identifiers.map((id) => ({
    identifier: id,
    content: notesById.get(id)?.content || '',
  }));

  const data = {
    ...parsedData.data,
    expertNotes: filledExpertNotes,
  };

  const effects: MissionAssessmentEffectCreateDto[] = (draft.effects || []).map((e) => {
    const parsedData = missionAssessmentEffectDataBaseSchema.safeParse(e.data || {});
    return {
      data: parsedData.success ? parsedData.data : {},
      domainId: e.domainId || '',
      effectId: e.effectId || '',
      secondaryEffects: e.secondaryEffects || [],
      targetSystems: e.targetSystems || [],
      useId: e.useId || '',
    };
  });

  const inters = (draft.inters || []).map((i) => {
    const fullInter = i as {
      categoryId?: string;
      country?: string;
      description?: string;
      digitalIntegrationIds?: string[];
      fieldedStatus?: FieldedStatus | null;
      mrl?: number | null;
      name?: string;
      notes?: string;
      operationalIntegrationIds?: string[];
      physicalIntegrationIds?: string[];
      priorityRationale?: string;
      priorityScore?: number | null;
      reference?: string;
      subcategoryId?: string;
      trl?: number | null;
      type?: MissionAssessmentInterType;
      verificationConfidence?: VerificationConfidence | null;
      verificationNotes?: string;
    };
    return {
      categoryId: fullInter.categoryId || '',
      country: fullInter.country || '',
      description: fullInter.description || '',
      digitalIntegrationIds: fullInter.digitalIntegrationIds || [],
      fieldedStatus: fullInter.fieldedStatus ?? undefined,
      mrl: fullInter.mrl ?? undefined,
      name: fullInter.name || '',
      notes: fullInter.notes || '',
      operationalIntegrationIds: fullInter.operationalIntegrationIds || [],
      physicalIntegrationIds: fullInter.physicalIntegrationIds || [],
      priorityRationale: fullInter.priorityRationale || '',
      priorityScore: fullInter.priorityScore ?? undefined,
      reference: fullInter.reference || '',
      subcategoryId: fullInter.subcategoryId || '',
      trl: fullInter.trl ?? undefined,
      type: fullInter.type || MissionAssessmentInterType.INTEROPERABLE,
      verificationConfidence: fullInter.verificationConfidence ?? undefined,
      verificationNotes: fullInter.verificationNotes || '',
    };
  });

  const recommendedServices = (draft.recommendedServices || []).map((s) => ({
    serviceId: s.serviceId || '',
    unit: s.unit || '',
  }));

  const taskRecommendations = (draft.taskRecommendations || []).map((t) => ({
    description: t.description || '',
  }));

  const recommendedTechnologyUses = (draft.recommendedTechnologyUses || []).map((tu) => ({
    description: tu.description || '',
    domains: (tu.domainIds || []).map((domainId) => ({ domainId })),
    useId: tu.useId || '',
  }));

  return {
    ...draft,
    capabilities: draft.capabilities || [],
    communities: draft.communities || [],
    data,
    effects,
    inters,
    killChains: draft.killChains || [],
    recommendedDomains: draft.recommendedDomains || [],
    recommendedServices,
    recommendedTechnologyUses,
    recommendedUses: draft.recommendedUses || [],
    taskRecommendations,
  } satisfies MissionAssessmentFormData;
};
